// Filo: araçlar, belgeler, bakım, uyum merkezi, QR/NFC cihazları (C26–C30, C69–C73): Zod girdileri ve yanıt tipleri
// (API ve konsol aynı tipi kullanır). Belge uçları (`/v1/documents`) şoför/rehber ekranları için de ortak sözleşmedir.
import { z } from 'zod';
import {
  DOCUMENT_STATUSES,
  DOCUMENT_TYPES,
  DRIVER_DOCUMENTS,
  PRODUCT_DOMAIN,
  VEHICLE_DOCUMENTS,
  type Direction,
  type DocumentStatus,
  type DocumentType,
  type TripStatus,
} from '../enums';
import type { TripDisplay } from '../state';
import { IsoDate, type Paged } from './common';

const page = z.coerce.number().int().min(1).default(1);
const size = (d: number) => z.coerce.number().int().min(1).max(200).default(d);
const search = z.string().trim().max(80).optional();

// ───────── belgeler (C29, C69, C70; şoför/rehber belgeleri de aynı uçları kullanır) ─────────

export const DOCUMENT_OWNER_TYPES = ['DRIVER', 'GUIDE', 'VEHICLE'] as const;
export type DocumentOwnerType = (typeof DOCUMENT_OWNER_TYPES)[number];
export const DOCUMENT_OWNER_LABELS: Record<DocumentOwnerType, string> = {
  DRIVER: 'Şoför',
  GUIDE: 'Rehber',
  VEHICLE: 'Araç',
};

/** Etkin belge durumu (tarihten türetilir): durum her yerde ikon + bu kelimeyle gösterilir. */
export const DOCUMENT_STATUS_LABELS: Record<DocumentStatus, string> = {
  PENDING: 'Onay bekliyor',
  VALID: 'Geçerli',
  EXPIRING: 'Süresi yaklaşıyor',
  EXPIRED: 'Süresi doldu',
  REJECTED: 'Reddedildi',
};

/** Sahip türüne göre eklenebilecek belge türleri (API doğrular). */
export const DOCUMENT_TYPES_BY_OWNER: Record<DocumentOwnerType, DocumentType[]> = {
  DRIVER: DRIVER_DOCUMENTS,
  GUIDE: ['ID_CARD', 'PHOTO', 'CRIMINAL_RECORD', 'PSYCHOTECHNIC'],
  VEHICLE: VEHICLE_DOCUMENTS,
};

/**
 * Uyum için zorunlu belgeler (eksikse uyum merkezinde "eksik" sayılır). Araçta kasko isteğe bağlı; şoför/rehber
 * listesi kişi ekranlarındaki doğrulama rozetiyle aynıdır (fotoğraf profil öğesi).
 */
export const REQUIRED_DOCUMENTS_BY_OWNER: Record<DocumentOwnerType, DocumentType[]> = {
  VEHICLE: ['REGISTRATION', 'INSPECTION', 'INSURANCE', 'ROUTE_PERMIT'],
  DRIVER: ['LICENSE', 'SRC', 'PSYCHOTECHNIC', 'ID_CARD', 'CRIMINAL_RECORD'],
  GUIDE: ['ID_CARD', 'CRIMINAL_RECORD'],
};

/** Bu kadar gün (ve altı) kalan geçerli belge "süresi yaklaşıyor" sayılır. */
export const DOCUMENT_EXPIRING_DAYS = 30;
const DAY_MS = 86_400_000;

/** Kalan gün: geçerlilik bitişi − bugün (İstanbul takvim günü; `today` YYYY-AA-GG). Negatifse süresi dolmuş. */
export function documentDaysLeft(validUntil: string | null | undefined, today: string): number | null {
  if (!validUntil) return null;
  return Math.round((Date.parse(`${validUntil.slice(0, 10)}T00:00:00Z`) - Date.parse(`${today}T00:00:00Z`)) / DAY_MS);
}

/** Saklanan durum + geçerlilik tarihinden etkin durum: onaylı belge için tarih belirleyicidir. */
export function documentStatusOf(stored: string, validUntil: string | null | undefined, today: string): DocumentStatus {
  if (stored === 'PENDING' || stored === 'REJECTED') return stored;
  const days = documentDaysLeft(validUntil, today);
  if (days === null) return 'VALID';
  return days < 0 ? 'EXPIRED' : days <= DOCUMENT_EXPIRING_DAYS ? 'EXPIRING' : 'VALID';
}

export const DocumentListQuery = z.object({
  ownerType: z.enum(DOCUMENT_OWNER_TYPES).optional(),
  ownerId: z.string().max(64).optional(),
  /** etkin durum (tarihten türetilmiş) */
  status: z.enum(DOCUMENT_STATUSES).optional(),
  type: z.enum(DOCUMENT_TYPES).optional(),
  /** onaylı ve bitişine en çok N gün kalan (süresi dolmuşlar dahil) */
  expiringWithinDays: z.coerce.number().int().min(0).max(366).optional(),
  q: search,
  /** `true`: yenisiyle değiştirilmiş eski belgeler de listelenir (varsayılan yalnız güncel belgeler) */
  history: z.enum(['true', 'false']).optional(),
  page,
  pageSize: size(50),
});
export type DocumentListQuery = z.infer<typeof DocumentListQuery>;

export const DocumentCreateInput = z
  .object({
    ownerType: z.enum(DOCUMENT_OWNER_TYPES),
    ownerId: z.string().min(1).max(64),
    type: z.enum(DOCUMENT_TYPES),
    number: z.string().trim().max(40).optional(),
    validFrom: IsoDate.optional(),
    validUntil: IsoDate.optional(),
    /** önce `POST /v1/uploads` (purpose=DOCUMENT) ile yüklenen dosya */
    fileId: z.string().max(64).optional(),
  })
  .refine((d) => !d.validFrom || !d.validUntil || d.validFrom <= d.validUntil, {
    message: 'Başlangıç tarihi bitiş tarihinden sonra olamaz.',
    path: ['validUntil'],
  });
export type DocumentCreateInput = z.infer<typeof DocumentCreateInput>;

export const DocumentUpdateInput = z.object({
  /** inceleme: yalnız `compliance:approve` izniyle */
  status: z.enum(['VALID', 'REJECTED']).optional(),
  number: z.string().trim().max(40).nullable().optional(),
  validFrom: IsoDate.nullable().optional(),
  validUntil: IsoDate.nullable().optional(),
  fileId: z.string().max(64).nullable().optional(),
  /** red sebebi / inceleme notu (denetim kaydına yazılır) */
  note: z.string().trim().max(300).optional(),
});
export type DocumentUpdateInput = z.infer<typeof DocumentUpdateInput>;

export interface DocumentItem {
  id: string;
  ownerType: DocumentOwnerType;
  ownerId: string;
  /** plaka ya da "Ad Soyad" */
  ownerLabel: string;
  vehicleId: string | null;
  driverId: string | null;
  guideId: string | null;
  type: DocumentType;
  typeLabel: string;
  number: string | null;
  /** YYYY-AA-GG */
  validFrom: string | null;
  /** YYYY-AA-GG */
  validUntil: string | null;
  /** etkin durum */
  status: DocumentStatus;
  daysLeft: number | null;
  fileId: string | null;
  /** kısa ömürlü imzalı bağlantı */
  fileUrl: string | null;
  /** inceleyen kişinin adı (onay/ret) */
  reviewedBy: string | null;
  createdAt: string;
}

export interface MissingDocument {
  ownerType: DocumentOwnerType;
  ownerId: string;
  ownerLabel: string;
  type: DocumentType;
  typeLabel: string;
}

// ───────── uyum merkezi (C69) ─────────

export interface ComplianceMonth {
  /** YYYY-AA; süresi dolmuşlar için `expired` */
  key: string;
  label: string;
  count: number;
  byOwner: Record<DocumentOwnerType, number>;
  /** en yakın bitenler (en çok 8) */
  items: DocumentItem[];
}

export interface ComplianceSummary {
  today: string;
  counts: { expired: number; expiring: number; pending: number; valid: number; missing: number; total: number };
  byOwner: {
    ownerType: DocumentOwnerType;
    label: string;
    total: number;
    expired: number;
    expiring: number;
    pending: number;
    missing: number;
  }[];
  /** süresi dolmuşlar + bu ay ve sonraki 5 ay */
  timeline: ComplianceMonth[];
  /** onay bekleyen yüklemeler (eskiden yeniye) */
  pending: DocumentItem[];
  /** süresi dolmuş ya da 7 gün içinde bitecek */
  urgent: DocumentItem[];
  missing: MissingDocument[];
}

// ───────── araçlar (C26–C28) ─────────

export const VEHICLE_STATUSES = ['ACTIVE', 'MAINTENANCE', 'INACTIVE'] as const;
export type VehicleStatus = (typeof VEHICLE_STATUSES)[number];
export const VEHICLE_STATUS_LABELS: Record<VehicleStatus, string> = {
  ACTIVE: 'Aktif',
  MAINTENANCE: 'Bakımda',
  INACTIVE: 'Pasif',
};

export const FUEL_TYPES = ['DIESEL', 'GASOLINE', 'LPG', 'HYBRID', 'ELECTRIC'] as const;
export type FuelType = (typeof FUEL_TYPES)[number];
export const FUEL_TYPE_LABELS: Record<FuelType, string> = {
  DIESEL: 'Dizel',
  GASOLINE: 'Benzin',
  LPG: 'LPG',
  HYBRID: 'Hibrit',
  ELECTRIC: 'Elektrik',
};

/** Aracın en kötü belge durumu (önem sırası: süresi dolmuş > eksik > yaklaşan > onay bekleyen > tamam). */
export const VEHICLE_DOC_STATES = ['EXPIRED', 'MISSING', 'EXPIRING', 'PENDING', 'OK'] as const;
export type VehicleDocState = (typeof VEHICLE_DOC_STATES)[number];
export const VEHICLE_DOC_STATE_LABELS: Record<VehicleDocState, string> = {
  EXPIRED: 'Süresi doldu',
  MISSING: 'Eksik belge',
  EXPIRING: 'Süresi yaklaşıyor',
  PENDING: 'Onay bekliyor',
  OK: 'Tamam',
};

export const VehicleListQuery = z.object({
  status: z.enum(VEHICLE_STATUSES).optional(),
  /** `ISSUE`: belgesi tamam olmayan tüm araçlar */
  docs: z.enum(['ISSUE', 'EXPIRED', 'MISSING', 'EXPIRING', 'PENDING']).optional(),
  q: search,
  page,
  pageSize: size(50),
});
export type VehicleListQuery = z.infer<typeof VehicleListQuery>;

const plate = z.string().trim().min(4, 'Plakayı 34 ABC 123 biçiminde yazın.').max(12);

export const VehicleInput = z.object({
  plate,
  make: z.string().trim().min(2, 'Markayı yazın.').max(40),
  model: z.string().trim().min(1, 'Modeli yazın.').max(60),
  year: z.number().int().min(1990, 'Model yılı 1990 ve sonrası olmalı.').max(2100).nullable().optional(),
  capacity: z.number().int().min(1, 'Kapasite en az 1 olmalı.').max(80, 'Kapasite en çok 80 olabilir.'),
  color: z.string().trim().max(30).optional(),
  fuelType: z.enum(FUEL_TYPES).optional(),
  odometerKm: z.number().int().min(0).max(5_000_000).optional(),
  status: z.enum(VEHICLE_STATUSES).optional(),
});
export type VehicleInput = z.infer<typeof VehicleInput>;

export const VehicleUpdateInput = VehicleInput.partial().extend({
  /** durum değişikliği notu (denetim kaydına yazılır) */
  note: z.string().trim().max(200).optional(),
});
export type VehicleUpdateInput = z.infer<typeof VehicleUpdateInput>;

export interface VehicleTripRef {
  tripId: string;
  date: string;
  direction: Direction;
  routeId: string;
  routeCode: string;
  routeName: string;
  institution: string;
  plannedStartAt: string;
  plannedEndAt: string;
  status: TripStatus;
  display: TripDisplay;
  delayMin: number;
  driverId: string;
  driver: string;
  distanceKm: number | null;
}

export interface NextMaintenance {
  id: string;
  type: string;
  typeLabel: string;
  dueAt: string | null;
  dueKm: number | null;
  daysLeft: number | null;
  kmLeft: number | null;
  overdue: boolean;
}

export interface VehicleRow {
  id: string;
  plate: string;
  make: string;
  model: string;
  year: number | null;
  capacity: number;
  color: string;
  fuelType: string;
  status: VehicleStatus;
  odometerKm: number;
  docState: VehicleDocState;
  /** en önemli belge sorunu: "Trafik sigortası 5 gün içinde bitiyor" */
  docIssue: string | null;
  nextMaintenance: NextMaintenance | null;
  /** şu anki ya da sıradaki (yoksa son) bugünkü sefer */
  today: VehicleTripRef | null;
  todayTrips: number;
}

export interface VehicleListResult extends Paged<VehicleRow> {
  counts: { all: number; active: number; maintenance: number; inactive: number; docIssue: number };
}

export interface VehicleDetail extends VehicleRow {
  co2PerKmG: number;
  createdAt: string;
  documents: DocumentItem[];
  missingDocuments: { type: DocumentType; label: string }[];
  maintenance: { open: MaintenanceItem[]; history: MaintenanceItem[] };
  /** bugünkü ve son seferler (yeniden eskiye) */
  trips: VehicleTripRef[];
  stickers: QrStickerItem[];
  devices: DeviceItem[];
  position: { lat: number; lng: number; heading: number; at: string | null; tripId: string | null } | null;
  usage: {
    trips30d: number;
    km30d: number;
    activeDays30d: number;
    /** son 30 gün, eskiden yeniye */
    daily: { date: string; trips: number; km: number }[];
    drivers30d: { driverId: string; name: string; trips: number }[];
    complaints90d: number;
  };
}

// ───────── bakım (C30) ─────────

export const MAINTENANCE_TYPES = ['PERIODIC', 'TIRE', 'BRAKE', 'REPAIR', 'INSPECTION'] as const;
export type MaintenanceType = (typeof MAINTENANCE_TYPES)[number];
export const MAINTENANCE_TYPE_LABELS: Record<MaintenanceType, string> = {
  PERIODIC: 'Periyodik bakım',
  TIRE: 'Lastik',
  BRAKE: 'Fren',
  REPAIR: 'Onarım',
  INSPECTION: 'Araç muayenesi',
};

export const MAINTENANCE_STATUSES = ['UPCOMING', 'OVERDUE', 'COMPLETED'] as const;
export type MaintenanceStatus = (typeof MAINTENANCE_STATUSES)[number];
export const MAINTENANCE_STATUS_LABELS: Record<MaintenanceStatus, string> = {
  UPCOMING: 'Planlandı',
  OVERDUE: 'Gecikti',
  COMPLETED: 'Tamamlandı',
};
/** "yaklaşıyor" eşikleri */
export const MAINTENANCE_DUE_SOON_DAYS = 7;
export const MAINTENANCE_DUE_SOON_KM = 1000;

export interface MaintenanceItem {
  id: string;
  vehicleId: string;
  plate: string;
  vehicleModel: string;
  vehicleStatus: VehicleStatus;
  type: string;
  typeLabel: string;
  /** etkin durum: tarih geçtiyse ya da kilometre aşıldıysa gecikti */
  status: MaintenanceStatus;
  dueSoon: boolean;
  dueAt: string | null;
  dueKm: number | null;
  odometerKm: number;
  daysLeft: number | null;
  kmLeft: number | null;
  completedAt: string | null;
  costKurus: number | null;
  vendor: string | null;
  notes: string | null;
}

export const MaintenanceListQuery = z.object({
  tab: z.enum(['upcoming', 'overdue', 'completed']).default('upcoming'),
  vehicleId: z.string().max(64).optional(),
  type: z.enum(MAINTENANCE_TYPES).optional(),
  page,
  pageSize: size(50),
});
export type MaintenanceListQuery = z.infer<typeof MaintenanceListQuery>;

export interface MaintenanceListResult extends Paged<MaintenanceItem> {
  counts: { upcoming: number; overdue: number; dueSoon: number; completed: number; cost30dKurus: number };
}

export const MaintenancePlanInput = z
  .object({
    vehicleId: z.string().min(1).max(64),
    type: z.enum(MAINTENANCE_TYPES),
    dueAt: IsoDate.optional(),
    dueKm: z.number().int().min(0).max(5_000_000).optional(),
    vendor: z.string().trim().max(80).optional(),
    notes: z.string().trim().max(300).optional(),
  })
  .refine((d) => !!d.dueAt || d.dueKm !== undefined, {
    message: 'Bakım tarihi ya da kilometresi girin.',
    path: ['dueAt'],
  });
export type MaintenancePlanInput = z.infer<typeof MaintenancePlanInput>;

export const MaintenanceUpdateInput = z.object({
  type: z.enum(MAINTENANCE_TYPES).optional(),
  dueAt: IsoDate.nullable().optional(),
  dueKm: z.number().int().min(0).max(5_000_000).nullable().optional(),
  vendor: z.string().trim().max(80).nullable().optional(),
  notes: z.string().trim().max(300).nullable().optional(),
});
export type MaintenanceUpdateInput = z.infer<typeof MaintenanceUpdateInput>;

export const MaintenanceCompleteInput = z.object({
  completedAt: IsoDate,
  costKurus: z.number().int().min(0).max(100_000_000).optional(),
  vendor: z.string().trim().max(80).optional(),
  /** bakımdaki kilometre; araç sayacından büyükse sayaç güncellenir */
  odometerKm: z.number().int().min(0).max(5_000_000).optional(),
  notes: z.string().trim().max(300).optional(),
  /** sıradaki bakımı da planla (aynı tür) */
  next: z.object({ dueAt: IsoDate.optional(), dueKm: z.number().int().min(0).max(5_000_000).optional() }).optional(),
});
export type MaintenanceCompleteInput = z.infer<typeof MaintenanceCompleteInput>;

// ───────── cihazlar (C71, C73) ─────────

export const DEVICE_TYPES = ['QR_SCANNER', 'NFC_READER', 'GPS_TRACKER', 'DRIVER_PHONE'] as const;
export type DeviceType = (typeof DEVICE_TYPES)[number];
export const DEVICE_TYPE_LABELS: Record<DeviceType, string> = {
  QR_SCANNER: 'QR okuyucu',
  NFC_READER: 'NFC okuyucu',
  GPS_TRACKER: 'GPS cihazı',
  DRIVER_PHONE: 'Şoför telefonu',
};
export const DEVICE_STATUSES = ['ONLINE', 'OFFLINE', 'RETIRED'] as const;
export type DeviceStatus = (typeof DEVICE_STATUSES)[number];
export const DEVICE_STATUS_LABELS: Record<DeviceStatus, string> = {
  ONLINE: 'Çevrimiçi',
  OFFLINE: 'Çevrimdışı',
  RETIRED: 'Kullanım dışı',
};
/** bu yüzdenin altı "pil zayıf" */
export const DEVICE_LOW_BATTERY = 20;

export const DeviceListQuery = z.object({
  type: z.enum(DEVICE_TYPES).optional(),
  status: z.enum(DEVICE_STATUSES).optional(),
  vehicleId: z.string().max(64).optional(),
  q: search,
  page,
  pageSize: size(50),
});
export type DeviceListQuery = z.infer<typeof DeviceListQuery>;

export const DeviceCreateInput = z.object({
  type: z.enum(['QR_SCANNER', 'NFC_READER', 'GPS_TRACKER']),
  serial: z
    .string()
    .trim()
    .min(3, 'Seri numarasını yazın.')
    .max(40)
    .regex(/^[A-Za-z0-9-]+$/, 'Seri numarası yalnız harf, rakam ve tire içerebilir.'),
  firmware: z.string().trim().max(20).optional(),
  vehicleId: z.string().max(64).nullable().optional(),
});
export type DeviceCreateInput = z.infer<typeof DeviceCreateInput>;

export const DeviceUpdateInput = z.object({
  vehicleId: z.string().max(64).nullable().optional(),
  /** true: kullanım dışı bırak · false: yeniden kullanıma al (ilk sinyale kadar çevrimdışı) */
  retired: z.boolean().optional(),
});
export type DeviceUpdateInput = z.infer<typeof DeviceUpdateInput>;

export interface DeviceItem {
  id: string;
  type: DeviceType;
  typeLabel: string;
  serial: string;
  firmware: string | null;
  status: DeviceStatus;
  battery: number | null;
  lastSeenAt: string | null;
  vehicleId: string | null;
  plate: string | null;
}

export interface DeviceListResult extends Paged<DeviceItem> {
  counts: { total: number; online: number; offline: number; lowBattery: number; unassigned: number };
}

export interface NfcSummary {
  cards: { total: number; active: number; lost: number; blocked: number };
  byInstitution: { institutionId: string; name: string; total: number; active: number }[];
  readers: { total: number; online: number; vehicles: number; activeVehicles: number };
}

// ───────── QR sticker (C72, F01) ─────────

export const STICKER_STATUSES = ['ACTIVE', 'INACTIVE'] as const;
export type StickerStatus = (typeof STICKER_STATUSES)[number];
export const STICKER_STATUS_LABELS: Record<StickerStatus, string> = {
  ACTIVE: 'Etkin',
  INACTIVE: 'Devre dışı',
};
/** Yolcu uygulaması kodu bu adresten çözer (P10). */
export const STICKER_BASE_URL = `https://${PRODUCT_DOMAIN}/v/`;

export interface QrStickerItem {
  id: string;
  vehicleId: string;
  plate: string;
  vehicleStatus: VehicleStatus;
  code: string;
  url: string;
  status: StickerStatus;
  printedAt: string | null;
  createdAt: string;
}

export const StickerListQuery = z.object({
  status: z.enum(STICKER_STATUSES).optional(),
  q: search,
  page,
  pageSize: size(50),
});
export type StickerListQuery = z.infer<typeof StickerListQuery>;

export interface StickerListResult extends Paged<QrStickerItem> {
  counts: { active: number; inactive: number; notPrinted: number; vehiclesWithout: number };
  /** etkin sticker'ı olmayan (pasif olmayan) araçlar */
  vehiclesWithout: { vehicleId: string; plate: string; model: string }[];
}

/** Yeni sticker üret: aracın etkin sticker'ı varsa devre dışı kalır (yeniden üretim). */
export const StickerCreateInput = z.object({ vehicleId: z.string().min(1).max(64) });
export type StickerCreateInput = z.infer<typeof StickerCreateInput>;

export const StickerUpdateInput = z.object({
  /** yalnız devre dışı bırakılabilir; yeniden etkinleştirme yerine yeni sticker üretilir */
  status: z.literal('INACTIVE').optional(),
  printed: z.literal(true).optional(),
});
export type StickerUpdateInput = z.infer<typeof StickerUpdateInput>;
